/**
 * Shared BOQ helpers used across BOQTable, BOQQuoteDetail, and PublicBOQQuoteView.
 * Avoids duplicating unit maps and chapter-grouping logic.
 */

// Map cost type names to short display units
export const UNIT_MAP = {
  'מחיר קבוע': 'קבוע',
  'מחיר למטר': 'מ׳',
  'מחיר למטר רבוע': 'מ״ר',
  'מחיר ליחידה': 'יח׳',
  'מחיר חודשי': 'חודש',
};

/**
 * Get short unit label for a cost type name.
 * Falls back to the full name if no short form exists.
 */
export const getUnitLabel = costTypeName => {
  if (!costTypeName) return '—';
  return UNIT_MAP[costTypeName] || costTypeName;
};

// Keys in selected_option that are metadata, not user-visible option selections
const OPTION_METADATA_KEYS = new Set([
  'type',
  'value',
  'cost',
  'notes',
  'selected_child_id',
  'source',
]);

/**
 * Extract display text from a selected_option value (handles string or {name:...} object)
 */
export const getOptionDisplayValue = val => {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val.name) return val.name;
  return null;
};

/**
 * Build a human-readable string from selected_option JSONB.
 * e.g. {"סוג": "סטנדרט", "תלייה": "תקרתית"} → "סוג: סטנדרט, תלייה: תקרתית"
 * If key is "default", skip the label and just show the value.
 */
export const formatSelectedOption = selectedOption => {
  if (!selectedOption || typeof selectedOption !== 'object') return '';
  const entries = Object.entries(selectedOption);
  if (entries.length === 0) return '';
  const parts = entries
    .filter(([key]) => !OPTION_METADATA_KEYS.has(key))
    .map(([key, val]) => {
      const displayVal = getOptionDisplayValue(val);
      if (!displayVal) return null;
      if (key === 'default') return displayVal;
      return `${key}: ${displayVal}`;
    })
    .filter(Boolean);
  return parts.join(', ');
};

// Cost types where the price is per-unit (i.e. line total = price × qty)
export const PER_UNIT_COST_TYPES = new Set([
  'מחיר למטר',
  'מחיר למטר רבוע',
  'מחיר ליחידה',
  'מחיר חודשי',
]);

/**
 * Calculate line total for a quote item.
 * For per-unit cost types: price × quantity. For fixed: just the price.
 * @param {number|null} price - The line price
 * @param {number} qty - The quantity
 * @param {string} costTypeName - The cost type name (e.g. 'מחיר ליחידה')
 * @returns {number} The line total
 */
export const getLineTotal = (price, qty, costTypeName) => {
  if (price == null || isNaN(price)) return 0;
  return PER_UNIT_COST_TYPES.has(costTypeName) ? price * (qty || 1) : price;
};

/**
 * Walk a chapter hierarchy upward to find the top-level (root) chapter.
 * @param {string} chapterId - Starting chapter ID
 * @param {Map} chapterById - Map of chapter ID → chapter object
 * @returns {Object|null} Top-level chapter or null
 */
export const getTopLevelChapter = (chapterId, chapterById) => {
  if (!chapterId) return null;
  let current = chapterById.get(chapterId);
  if (!current) return null;
  while (current.parent_id && chapterById.get(current.parent_id)) {
    current = chapterById.get(current.parent_id);
  }
  return current;
};
